/*
 * See https://docs.imgix.com/apis/rendering/format/fm and https://docs.imgix.com/apis/rendering/size/fit for details on the parameters.
 *
 * Note that the images are always produced as JPEGs, even if the original format of the image was different - this is to ensure
 * a balance between a small file size, wide support in browsers and good image quality.
 *
 * Note that the images will never be produced at a larger resolution than the original file,
 * even if a larger resolution is requested by the one/two input dimensions. This ensures the images are not upscaled which
 * can degrade quality
 */
const BASE_QUERY_STRING: string = '?fm=jpg&fit=min'
// 40 is a nice balance between small file sizes and acceptable quality
const DEFAULT_QUALITY_PARAMETER: number = 40

/*
 * Images look blurry when rendered at the same size as their container. Increasing their dimensions by a factor of two
 * balances reducing bluriness (increasing sharpness) whilst auto retaining a small image size
 */
const DIMENSION_MULTIPLICATION_FACTOR: number = 2

interface Inputs {
  originalImgxImageUrl: string
  height?: number | undefined
  width?: number | undefined
  quality?: number | undefined
  disableDimensionMultiplicationFactor?: boolean | undefined
}

/**
 * Create the URL for a compressed imgx image based on an unformatted imgx URL without any existing query parameters, i.e. a pure URL.
 *
 * If only one of height or width is specified, the other dimension is calculated
 * by imgx so the compressed image uses the the same aspect ratio as the original image.
 * See https://docs.imgix.com/apis/rendering/size/h#:~:text=If%20only%20one%20dimension%20is%20specified,%20the%20other%20dimension%20will%20be%20calculated%20according%20to%20the%20aspect%20ratio%20of%20the%20input%20image
 *
 * By default, images are produced at twice the input dimension (height or width, or both),
 * which produces sharp images (especially on Retina displays with twice as many pixels) whilst also balancing a small file size.
 * This can be disabled by using `disableDimensionMultiplicationFactor` if it is not required.
 */
export const createCompressedImgxImageUrl = ({
  originalImgxImageUrl,
  height,
  width,
  quality,
  disableDimensionMultiplicationFactor,
}: Inputs): string =>
  `${originalImgxImageUrl}${BASE_QUERY_STRING}${
    height
      ? `&h=${
          height *
          (!disableDimensionMultiplicationFactor
            ? DIMENSION_MULTIPLICATION_FACTOR
            : 1)
        }`
      : ''
  }${
    width
      ? `&w=${
          width *
          (!disableDimensionMultiplicationFactor
            ? DIMENSION_MULTIPLICATION_FACTOR
            : 1)
        }`
      : ''
  }&q=${quality ?? DEFAULT_QUALITY_PARAMETER}`
